import type { CXoneAuth } from '@nice-devone/auth-sdk';
import type { CXoneClient } from '@nice-devone/agent-sdk';
import type {
  CXoneDigitalReplyRequest,
  CXoneFileUploadRequest,
  TypingIndicatorActions,
} from '@nice-devone/common-sdk';
import type {
  CXoneDigitalClient,
  CXoneDigitalContact,
} from '@nice-devone/digital-sdk';
import type {
  CxoneAgentConfig,
  CxoneAgentEventInput,
} from '@servicesai/api-client';
import type {
  CxoneAgentIdentity,
  CxoneSdkContact,
  CxoneSdkMessage,
  CxoneSdkSnapshot,
  CxoneSdkStatus,
} from './types.js';

type RecordValue = Record<string, unknown>;
const AUTH_STATE_KEY = 'servicesai.cxone.oidcState';

function record(value: unknown): RecordValue {
  return value && typeof value === 'object' ? (value as RecordValue) : {};
}

function stringValue(...values: unknown[]): string | undefined {
  return values.find((value) => typeof value === 'string' && value.trim()) as
    | string
    | undefined;
}

function nested(value: unknown, ...path: string[]): unknown {
  let current = value;
  for (const key of path) current = record(current)[key];
  return current;
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function messageText(value: unknown): string {
  return (
    stringValue(
      nested(value, 'messageContent', 'payload', 'text'),
      nested(value, 'messageContent', 'text'),
      nested(value, 'content', 'text'),
      record(value).text,
    ) ?? ''
  );
}

function mapMessage(value: unknown): CxoneSdkMessage {
  const author = record(record(value).author);
  const authorIdentity = record(record(value).authorEndUserIdentity);
  const attachments = arrayValue(
    nested(value, 'messageContent', 'attachments') ?? record(value).attachments,
  ).map((attachment) => ({
    name:
      stringValue(
        record(attachment).friendlyName,
        record(attachment).name,
        record(attachment).fileName,
      ) ?? 'Attachment',
    url: stringValue(record(attachment).url, record(attachment).mediaUrl) ?? '',
    mime: stringValue(record(attachment).mimeType, record(attachment).contentType),
  }));
  const direction =
    record(value).isOutbound === true ||
    stringValue(record(value).direction, record(value).messageDirection)?.toLowerCase() ===
      'outbound'
      ? 'outbound'
      : 'inbound';
  return {
    id:
      stringValue(record(value).id, record(value).messageId, record(value).interactionId) ??
      crypto.randomUUID(),
    direction,
    text: messageText(value),
    createdAt: stringValue(
      record(value).createdAt,
      record(value).createdDate,
      record(value).timestamp,
    ),
    authorName: stringValue(
      author.fullName,
      author.name,
      authorIdentity.fullName,
      authorIdentity.firstName,
    ),
    attachments: attachments.filter((attachment) => Boolean(attachment.url)),
  };
}

function mapContact(value: CXoneDigitalContact): CxoneSdkContact {
  const raw = record(value);
  const caseValue = record(raw.case);
  const channel = record(raw.channel);
  const routingQueue = record(caseValue.routingQueue ?? raw.routingQueue);
  const inboxAssignee = record(caseValue.inboxAssignee ?? raw.inboxAssignee);
  const customer = record(caseValue.customer ?? raw.customer);
  const thread = record(caseValue.thread ?? raw.thread);
  const messages = arrayValue(raw.messages ?? caseValue.messages).map(mapMessage);
  const inboundMessage = [...arrayValue(raw.messages ?? caseValue.messages)]
    .reverse()
    .find((message) => mapMessage(message).direction === 'inbound');
  const identity = record(record(inboundMessage).authorEndUserIdentity);
  const recipient = record(
    arrayValue(caseValue.endUserRecipients)[0] ??
      caseValue.authorEndUserIdentity ??
      identity,
  );
  return {
    id:
      stringValue(raw.caseId, raw.id, caseValue.id, caseValue.caseId) ??
      crypto.randomUUID(),
    externalThreadId: stringValue(
      thread.idOnExternalPlatform,
      caseValue.threadIdOnExternalPlatform,
      raw.threadIdOnExternalPlatform,
      caseValue.externalThreadId,
    ),
    channelId:
      stringValue(raw.channelId, channel.id, caseValue.channelId) ?? '',
    channelName: stringValue(channel.name, channel.channelName),
    status: stringValue(raw.status, caseValue.status) ?? 'open',
    skillId: stringValue(
      raw.skillId,
      caseValue.skillId,
      routingQueue.skillId,
    ),
    routingQueueId: stringValue(
      raw.routingQueueId,
      caseValue.routingQueueId,
      routingQueue.id,
    ),
    routingQueueName: stringValue(routingQueue.name),
    customerName: stringValue(
      customer.fullName,
      customer.name,
      recipient.fullName,
      recipient.firstName,
    ),
    recipientId: stringValue(recipient.idOnExternalPlatform, recipient.id),
    recipientName: stringValue(recipient.fullName, recipient.firstName, recipient.name),
    assigneeId: stringValue(inboxAssignee.id, inboxAssignee.userId),
    assigneeName: stringValue(inboxAssignee.fullName, inboxAssignee.name),
    messages,
    updatedAt: stringValue(
      raw.updatedAt,
      caseValue.updatedAt,
      caseValue.lastMessageAt,
    ),
  };
}

function identityFromUser(value: unknown): CxoneAgentIdentity {
  const user = record(value);
  const firstName = stringValue(user.firstName) ?? '';
  const lastName = stringValue(user.lastName) ?? '';
  return {
    agentId:
      String(user.icAgentId ?? user.agentId ?? user.userId ?? '').trim(),
    userId: stringValue(user.userId),
    email: stringValue(user.emailAddress, user.email, user.userName),
    displayName:
      stringValue(user.fullName, `${firstName} ${lastName}`.trim(), user.userName) ??
      'CXone agent',
  };
}

export interface CxoneBridgeHandlers {
  onSnapshot: (snapshot: CxoneSdkSnapshot) => void;
  onAuthorityEvent: (event: CxoneAgentEventInput) => void;
}

/**
 * Thin boundary around NICE's singleton SDKs. The rest of the console consumes
 * stable platform types and never reaches into undocumented SDK internals.
 */
export class CxoneSdkBridge {
  private auth: CXoneAuth | null = null;
  private agent: CXoneClient | null = null;
  private digital: CXoneDigitalClient | null = null;
  private fileUploadRequest:
    | (new () => CXoneFileUploadRequest)
    | null = null;
  private typingActions: typeof TypingIndicatorActions | null = null;
  private handlers: CxoneBridgeHandlers | null = null;
  private config: CxoneAgentConfig | null = null;
  private status: CxoneSdkStatus = 'loading';
  private identity: CxoneAgentIdentity | null = null;
  private contacts = new Map<string, CXoneDigitalContact>();
  private selectedContactId: string | null = null;
  private remoteTyping = new Set<string>();
  private subscriptions: Array<{ unsubscribe(): void }> = [];
  private subscribed = false;
  private digitalInitialized = false;
  private connectionMessage?: string;
  private error?: string;

  private async loadSdk(): Promise<void> {
    if (this.auth && this.agent && this.digital) return;
    const [authSdk, agentSdk, commonSdk, digitalSdk] = await Promise.all([
      import('@nice-devone/auth-sdk'),
      import('@nice-devone/agent-sdk'),
      import('@nice-devone/common-sdk'),
      import('@nice-devone/digital-sdk'),
    ]);
    this.auth = authSdk.CXoneAuth.instance;
    this.agent = agentSdk.CXoneClient.instance;
    this.digital = digitalSdk.CXoneDigitalClient.instance;
    this.fileUploadRequest = commonSdk.CXoneFileUploadRequest;
    this.typingActions = commonSdk.TypingIndicatorActions;
  }

  private sdk(): {
    auth: CXoneAuth;
    agent: CXoneClient;
    digital: CXoneDigitalClient;
  } {
    if (!this.auth || !this.agent || !this.digital) {
      throw new Error('CXone Agent SDK is not loaded');
    }
    return { auth: this.auth, agent: this.agent, digital: this.digital };
  }

  private initializeAuth(state: string): void {
    if (!this.config) throw new Error('CXone is not configured');
    this.sdk().auth.init({
      cxoneHostname: this.config.issuer,
      clientId: this.config.clientId,
      redirectUri: this.config.redirectUri,
      state,
    });
  }

  async initialize(
    config: CxoneAgentConfig,
    handlers: CxoneBridgeHandlers,
  ): Promise<void> {
    this.config = config;
    this.handlers = handlers;
    if (config.mode === 'mock') {
      this.setStatus('mock');
      return;
    }
    if (!config.configured || !config.issuer || !config.clientId || !config.redirectUri) {
      this.setStatus('not_configured');
      return;
    }
    await this.loadSdk();
    const { auth } = this.sdk();
    const authState = sessionStorage.getItem(AUTH_STATE_KEY) ?? crypto.randomUUID();
    sessionStorage.setItem(AUTH_STATE_KEY, authState);
    this.initializeAuth(authState);
    this.subscribe();
    try {
      await auth.restoreData();
      if (auth.getAuthState().isTokenValid) await this.startDigital();
      else this.setStatus('authentication_required');
    } catch (error) {
      this.setError(error);
    }
  }

  private subscribe(): void {
    if (this.subscribed) return;
    this.subscribed = true;
    const { auth } = this.sdk();
    this.subscriptions.push(
      auth.onAuthStatusChange.subscribe((event) => {
        if (event.status === 'Authenticating') {
          this.setStatus('authenticating');
        } else if (
          event.status === 'Authenticated' ||
          event.status === 'RefreshTokenSuccess'
        ) {
          void this.startDigital();
        } else if (event.status === 'NotAuthenticated') {
          this.setStatus('authentication_required');
        } else if (event.status === 'AuthenticatedFailed') {
          this.setError(new Error('CXone authentication failed'));
        }
      }),
    );
  }

  private subscribeDigital(): void {
    const manager = this.sdk().digital.digitalContactManager;
    this.subscriptions.push(
      manager.onDigitalContactEvent.subscribe((contact) => {
        this.upsertContact(contact, 'contact.updated');
      }),
      manager.onDigitalContactNewMessageEvent.subscribe((event) => {
        const contact = this.contacts.get(event.contactId);
        if (contact) this.upsertContact(contact, 'message.created');
      }),
      manager.onDigitalContactUserTypingPreviewEvent.subscribe((event) => {
        const contact = [...this.contacts.values()].find(
          (candidate) => mapContact(candidate).externalThreadId === event.threadId,
        );
        if (!contact) return;
        const contactId = mapContact(contact).id;
        const typing = Boolean(event.message);
        if (typing) this.remoteTyping.add(contactId);
        else this.remoteTyping.delete(contactId);
        this.emit();
      }),
      manager.onDigitalWsNotificationEvent.subscribe((message) => {
        this.connectionMessage = message;
        this.emit();
      }),
    );
  }

  private async startDigital(): Promise<void> {
    if (this.digitalInitialized) return;
    this.digitalInitialized = true;
    this.setStatus('connecting');
    try {
      const { agent, digital } = this.sdk();
      await agent.initAuthDependentModules();
      await digital.initDigitalEngagement();
      this.subscribeDigital();
      const user = digital.cxoneUser.getUserInfo();
      this.identity = identityFromUser(user);
      if (!this.identity.agentId) {
        const fetched = await digital.cxoneUser.getUserDetails();
        this.identity = identityFromUser(fetched);
      }
      if (!this.identity.agentId) throw new Error('CXone agent identity is unavailable');
      this.setStatus('ready');
    } catch (error) {
      this.digitalInitialized = false;
      this.setError(error);
    }
  }

  async login(displayMode: 'page' | 'popup' = 'page'): Promise<void> {
    if (!this.config) throw new Error('CXone is not configured');
    await this.loadSdk();
    const state = crypto.randomUUID();
    sessionStorage.setItem(AUTH_STATE_KEY, state);
    this.initializeAuth(state);
    this.setStatus('authenticating');
    const url = await this.sdk().auth.getAuthorizeUrl(
      displayMode,
      'S256',
      this.config.tenantId || undefined,
    );
    sessionStorage.setItem('servicesai.cxone.authMode', displayMode);
    if (displayMode === 'page') {
      window.location.assign(url);
      return;
    }
    const popup = window.open(
      url,
      'servicesai-cxone-auth',
      'width=540,height=680,scrollbars=yes,toolbar=no',
    );
    if (!popup) throw new Error('CXone login popup was blocked');
  }

  async completeAuthentication(code: string, state: string): Promise<void> {
    if (!this.config?.clientId) throw new Error('CXone client ID is not configured');
    const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    if (!expectedState || !state || state !== expectedState) {
      throw new Error('CXone authentication state validation failed');
    }
    await this.loadSdk();
    await this.sdk().auth.getAccessTokenByCode({
      clientId: this.config.clientId,
      code,
    });
    sessionStorage.removeItem(AUTH_STATE_KEY);
  }

  async logout(): Promise<void> {
    const { agent, digital } = this.sdk();
    digital.digitalContactManager.terminateDigitalWorkers();
    await digital.clearDigitalCache();
    await agent.clearCache();
    this.contacts.clear();
    this.identity = null;
    this.digitalInitialized = false;
    this.setStatus('authentication_required');
  }

  selectContact(contactId: string | null): void {
    this.selectedContactId = contactId;
    this.emit();
  }

  private getContact(contactId: string): {
    sdk: CXoneDigitalContact;
    contact: CxoneSdkContact;
  } {
    const sdk = this.contacts.get(contactId);
    if (!sdk) throw new Error('CXone contact is not assigned to this agent');
    return { sdk, contact: mapContact(sdk) };
  }

  async reply(contactId: string, text: string): Promise<void> {
    const { sdk, contact } = this.getContact(contactId);
    if (!contact.externalThreadId || !contact.recipientId || !contact.channelId) {
      throw new Error('CXone contact does not include a replyable channel recipient');
    }
    const request: CXoneDigitalReplyRequest = {
      thread: { idOnExternalPlatform: contact.externalThreadId },
      messageContent: { type: 'TEXT', payload: { text } },
      recipients: [
        {
          idOnExternalPlatform: contact.recipientId,
          name: contact.recipientName ?? contact.customerName ?? 'Citizen',
          isPrimary: true,
          isPrivate: false,
        },
      ],
    };
    await sdk.reply(request, contact.channelId, crypto.randomUUID());
  }

  async uploadAttachment(contactId: string, file: File, caption = ''): Promise<void> {
    if (!this.identity?.userId) throw new Error('CXone digital user ID is unavailable');
    const { sdk, contact } = this.getContact(contactId);
    if (!contact.externalThreadId || !contact.recipientId || !contact.channelId) {
      throw new Error('CXone contact does not include a replyable channel recipient');
    }
    const content = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = String(reader.result ?? '');
        resolve(value.includes(',') ? value.slice(value.indexOf(',') + 1) : value);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Unable to read attachment'));
      reader.readAsDataURL(file);
    });
    if (!this.fileUploadRequest) throw new Error('CXone upload module is unavailable');
    const upload = new this.fileUploadRequest();
    upload.content = content;
    upload.mimeType = file.type;
    const uploaded = await sdk.upload(upload, this.identity.userId);
    await sdk.reply(
      {
        thread: { idOnExternalPlatform: contact.externalThreadId },
        messageContent: {
          type: 'TEXT',
          payload: { text: caption },
        },
        attachments: [
          {
            friendlyName: file.name,
            url: uploaded.url,
            isInline: false,
          },
        ],
        recipients: [
          {
            idOnExternalPlatform: contact.recipientId,
            name: contact.recipientName ?? contact.customerName ?? 'Citizen',
            isPrimary: true,
            isPrivate: false,
          },
        ],
      },
      contact.channelId,
      crypto.randomUUID(),
    );
  }

  async setTyping(contactId: string, typing: boolean): Promise<void> {
    const { contact } = this.getContact(contactId);
    if (!contact.externalThreadId || !contact.channelId) return;
    if (!this.typingActions) throw new Error('CXone typing module is unavailable');
    await this.sdk().digital.digitalService.setTypingIndicatorForPatron(
      contact.channelId,
      contact.externalThreadId,
      typing
        ? this.typingActions.TYPING_ON
        : this.typingActions.TYPING_OFF,
    );
  }

  async transfer(contactId: string, routingQueueId: string): Promise<void> {
    const { sdk } = this.getContact(contactId);
    await sdk.changeRoutingQueue(routingQueueId);
  }

  async release(contactId: string): Promise<void> {
    const { sdk } = this.getContact(contactId);
    await sdk.unassign();
  }

  async resolve(contactId: string, dispositionId?: number): Promise<void> {
    const { sdk } = this.getContact(contactId);
    void dispositionId;
    await sdk.changeStatus('closed');
  }

  private upsertContact(contact: CXoneDigitalContact, eventKind: string): void {
    const mapped = mapContact(contact);
    this.contacts.set(mapped.id, contact);
    if (!this.selectedContactId) this.selectedContactId = mapped.id;
    this.handlers?.onAuthorityEvent({
      eventId: crypto.randomUUID(),
      eventKind,
      contactId: mapped.id,
      externalThreadId: mapped.externalThreadId,
      status: mapped.status,
      skillId: mapped.skillId,
      routingQueueId: mapped.routingQueueId,
      routingQueueName: mapped.routingQueueName,
      assignee: {
        id: this.identity?.agentId,
        name: mapped.assigneeName ?? this.identity?.displayName,
      },
      occurredAt: mapped.updatedAt ?? new Date().toISOString(),
    });
    this.emit();
  }

  private setStatus(status: CxoneSdkStatus): void {
    this.status = status;
    this.error = undefined;
    this.emit();
  }

  private setError(error: unknown): void {
    this.status = 'error';
    this.error = error instanceof Error ? error.message : String(error);
    this.emit();
  }

  private emit(): void {
    this.handlers?.onSnapshot({
      status: this.status,
      config: this.config,
      identity: this.identity,
      contacts: [...this.contacts.values()].map(mapContact),
      selectedContactId: this.selectedContactId,
      remoteTypingContactIds: [...this.remoteTyping],
      connectionMessage: this.connectionMessage,
      error: this.error,
    });
  }
}

export const cxoneSdkBridge = new CxoneSdkBridge();
